/**
 * AuthContext – Globaler Auth-Zustand für die gesamte IMHO-App.
 *
 * Speichert:
 *   - maToken (X-MA-Token für alle APIs)
 *   - sessionId (Rückwärtskompatibilität alte Session-Dateien)
 *   - user: { kuerzel, vorname, nachname, rolle, zeigt_zeiterfassung, zugang_typ, module[] }
 *   - authReady: true sobald Token-Check beim Start abgeschlossen
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";
import { API_BASE } from "../api/config";
import { ladeDarstellungVomServer } from "../utils/darstellung";

const AuthContext = createContext(null);

export function AuthProvider({ children, onAuthChange }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  // Beim Start: Token aus localStorage laden und prüfen
  useEffect(() => {
    const stored = localStorage.getItem("maToken");
    if (!stored) {
      setAuthReady(true);
      return;
    }
    fetch(`${API_BASE}/auth/me`, {
      headers: { "x-ma-token": stored },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) {
          setToken(stored);
          setUser(data);
          // Welle 99: Theme und Schriftgröße des Kontos holen, sobald die
          // Anmeldung steht. Bis dahin gilt der gemerkte Wert aus index.js.
          ladeDarstellungVomServer();
          if (onAuthChange) onAuthChange(true);
        } else {
          localStorage.removeItem("maToken");
          localStorage.removeItem("sessionId");
          Cookies.remove("sessionId");
          Cookies.remove("username");
        }
      })
      .catch(() => {})
      .finally(() => setAuthReady(true));
  }, []); // eslint-disable-line

  const login = useCallback(async (kuerzel, passwort) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kuerzel, passwort }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      // Sonderfall: nur_zeiterfassung User → Backend gibt 403 mit redirect-Info
      if (res.status === 403 && err.detail && typeof err.detail === "object" && err.detail.redirect) {
        // Token trotzdem lokal speichern damit ZeitApp funktioniert
        if (err.detail.token) {
          localStorage.setItem("maToken", err.detail.token);
          localStorage.setItem("username", err.detail.kuerzel || kuerzel);
        }
        const redirectErr = new Error("REDIRECT:" + err.detail.redirect);
        redirectErr.redirect = err.detail.redirect;
        throw redirectErr;
      }
      throw new Error(err.detail || "Login fehlgeschlagen");
    }
    const data = await res.json();

    // Alles speichern
    localStorage.setItem("maToken", data.token);
    localStorage.setItem("sessionId", data.sessionId);
    localStorage.setItem("username", data.kuerzel);
    Cookies.set("sessionId", data.sessionId, { expires: 30 });
    Cookies.set("username", data.kuerzel, { expires: 30 });
    // maToken auch als Cookie: authentifiziert Browser-Subrequests ohne Header
    // (VB-Report-Tab, eingebettete PDF-Links, Download-Anker)
    Cookies.set("maToken", data.token, { expires: 30 });

    // User-Info über /auth/me laden (enthält module[])
    const meRes = await fetch(`${API_BASE}/auth/me`, {
      headers: { "x-ma-token": data.token },
    });
    const me = meRes.ok ? await meRes.json() : data;

    setToken(data.token);
    setUser(me);
    ladeDarstellungVomServer();
    if (onAuthChange) onAuthChange(true);
    return me;
  }, [onAuthChange]);

  /** Login für /zeit App – nutzt /auth/login-zeit, kein sessionId */
  const loginZeit = useCallback(async (kuerzel, passwort) => {
    const res = await fetch(`${API_BASE}/auth/login-zeit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kuerzel, passwort }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Login fehlgeschlagen");
    }
    const data = await res.json();
    localStorage.setItem("maToken", data.token);
    Cookies.set("maToken", data.token, { expires: 30 });
    const me = { ...data, module: data.module || [] };
    setToken(data.token);
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    const t = token || localStorage.getItem("maToken");
    const sid = localStorage.getItem("sessionId");
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        headers: {
          "x-ma-token": t || "",
          "x-session-id": sid || "",
        },
      });
    } catch (_) {}

    localStorage.removeItem("maToken");
    localStorage.removeItem("sessionId");
    localStorage.removeItem("username");
    Cookies.remove("sessionId");
    Cookies.remove("username");
    Cookies.remove("maToken");

    setToken(null);
    setUser(null);
    if (onAuthChange) onAuthChange(false);
  }, [token, onAuthChange]);

  return (
    <AuthContext.Provider value={{ user, token, authReady, login, loginZeit, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth muss innerhalb von AuthProvider verwendet werden");
  return ctx;
}
